import type { Feature, FeatureCollection, Position } from "geojson";
import { polygon } from "@turf/helpers";
import type { AnyPolygonFeature, GeometryResult } from "../types";
import { ensureRingsClosed } from "./editHelpers";
import { isPolygonValid, polygonContains } from "./validate";
import { metresPerDegree, ringAreaSqM } from "./planar";

/**
 * Fewest positions a ring can have and still enclose anything. Field files routinely carry
 * collapsed rings below this, and turf's `polygon` throws on them rather than ignoring them —
 * so every ring is filtered before it reaches turf, not after.
 */
const MIN_RING_POSITIONS = 4;

/** One polygon part of a collection, addressed the way a polygon ID addresses it. */
export interface PartRef {
	featureIndex: number;
	pathIndex: number;
}

/** The rings of every polygon part of a feature, or `null` if it holds no polygons. */
function partsOf(feature: Feature): Position[][][] | null {
	const geometry = feature.geometry;
	if (geometry.type === "Polygon") return [geometry.coordinates];
	if (geometry.type === "MultiPolygon") return geometry.coordinates;
	return null;
}

/** Rebuild a feature around a new set of parts, dropping it entirely when none are left. */
function withParts(feature: Feature, parts: Position[][][]): Feature | null {
	if (parts.length === 0) return null;
	// Built from scratch rather than spread: a bbox left over from the old geometry would
	// outlive the edit and quietly fail turf's containment checks later.
	return {
		type: "Feature",
		properties: feature.properties,
		geometry:
			parts.length === 1 ? { type: "Polygon", coordinates: parts[0] } : { type: "MultiPolygon", coordinates: parts },
	};
}

/**
 * The rings of a part that actually enclose area, closed, or `null` if its outer ring does not.
 *
 * Closed before they are measured, for two reasons. `polygon` has a second throw of its own —
 * "First and last Position are not equivalent" — and this runs on every right-click from inside
 * a `useMemo`, where a throw unmounts the map rather than spoiling one menu entry. And a ring
 * that arrives without its repeated last position is one short of its real count, so a genuine
 * triangle written `[a, b, c]` would otherwise be discarded as collapsed.
 */
function enclosingRings(rings: Position[][]): Position[][] | null {
	const closed = ensureRingsClosed(rings);
	if (closed.length === 0 || closed[0].length < MIN_RING_POSITIONS) return null;
	return closed.filter((ring) => ring.length >= MIN_RING_POSITIONS);
}

/** Walk every polygon part of a collection that encloses area. */
function eachPart(fc: FeatureCollection, visit: (rings: Position[][], ref: PartRef) => void) {
	fc.features.forEach((feature, featureIndex) => {
		partsOf(feature)?.forEach((rings, pathIndex) => {
			const usable = enclosingRings(rings);
			if (usable) visit(usable, { featureIndex, pathIndex });
		});
	});
}

/**
 * The part that should swallow `source` as a hole: the smallest one that encloses it.
 *
 * Smallest, not first, because parts nest — an island inside a lake inside a field belongs to
 * the lake, and picking the field instead would punch a hole straight through it. `null` when
 * nothing encloses the part, which is the honest answer for a polygon out on its own.
 */
export function findHoleContainer(fc: FeatureCollection, source: PartRef): PartRef | null {
	const sourceFeature = fc.features[source.featureIndex];
	const rawRings = sourceFeature ? partsOf(sourceFeature)?.[source.pathIndex] : undefined;
	const sourceRings = rawRings ? enclosingRings(rawRings) : null;
	if (!sourceRings) return null;

	const sourcePoly = polygon([sourceRings[0]]);
	const scale = metresPerDegree(sourceRings[0][0][1]);

	let best: PartRef | null = null;
	let bestArea = Infinity;
	eachPart(fc, (rings, ref) => {
		if (ref.featureIndex === source.featureIndex && ref.pathIndex === source.pathIndex) return;
		if (!polygonContains(polygon(rings) as AnyPolygonFeature, sourcePoly as AnyPolygonFeature)) return;
		const area = ringAreaSqM(rings[0], scale);
		if (area < bestArea) {
			bestArea = area;
			best = ref;
		}
	});
	return best;
}

/**
 * Turn a polygon part into a hole in whichever part encloses it.
 *
 * The part stops being land in its own right and becomes a void in its container — what you
 * want for a pond, a stand of trees, or a waterway that arrived from the field file as its own
 * little island instead of as the exclusion it really is.
 */
export function convertPartToHole(fc: FeatureCollection, source: PartRef): GeometryResult<FeatureCollection> {
	const sourceFeature = fc.features[source.featureIndex];
	const rawRings = sourceFeature ? partsOf(sourceFeature)?.[source.pathIndex] : undefined;
	const sourceRings = rawRings ? enclosingRings(rawRings) : null;
	if (!sourceRings) return { ok: false, error: "That polygon is too small a shape to cut out of anything" };

	if (sourceRings.length > 1) {
		// Its holes would have to become islands floating in the new void, and only the person
		// looking at the map can say whether that is what they meant.
		return { ok: false, error: "This polygon has holes of its own — remove them before making it a hole" };
	}

	const container = findHoleContainer(fc, source);
	if (!container) {
		return { ok: false, error: "This polygon is not inside another one, so there is nothing to cut it out of" };
	}

	const partsByFeature = fc.features.map(partsOf);
	const containerParts = partsByFeature[container.featureIndex];
	if (!containerParts) return { ok: false, error: "That polygon is no longer on the map" };

	const containerRings = enclosingRings(containerParts[container.pathIndex]);
	if (!containerRings) return { ok: false, error: "That polygon is no longer on the map" };
	const holed = [...containerRings, sourceRings[0]];
	const check = isPolygonValid(polygon(holed) as AnyPolygonFeature);
	if (!check.isValid) return { ok: false, error: check.message ?? "That hole would not be a valid shape" };

	partsByFeature[container.featureIndex] = containerParts.map((rings, i) =>
		i === container.pathIndex ? holed : rings,
	);
	const sourceParts = partsByFeature[source.featureIndex];
	if (sourceParts) {
		partsByFeature[source.featureIndex] = sourceParts.filter((_, i) => i !== source.pathIndex);
	}

	const features = fc.features.flatMap((feature, i) => {
		const parts = partsByFeature[i];
		if (!parts) return [feature];
		const rebuilt = withParts(feature, parts);
		return rebuilt ? [rebuilt] : [];
	});

	return { ok: true, data: { type: "FeatureCollection", features } };
}
